import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import type {
  Admin,
  AuthResponse,
  CreateOnboardingInput,
  OnboardingAdmin,
} from '@scriptpad/shared';

const baseURL = import.meta.env.VITE_API_BASE ?? 'http://localhost:4000';

let accessToken: string | null = null;
let refreshing: Promise<AuthResponse | null> | null = null;
let onUnauthorised: (() => void) | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getAccessToken = (): string | null => accessToken;

export const setOnUnauthorised = (cb: () => void): void => {
  onUnauthorised = cb;
};

export const api = axios.create({ baseURL, withCredentials: true, timeout: 30000 });

api.interceptors.request.use((cfg) => {
  if (accessToken) {
    cfg.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return cfg;
});

/**
 * Single shared in-flight refresh. All callers (initial bootstrap,
 * 401-retry interceptor, StrictMode-double-invoked effects) coalesce onto
 * the same network request so we never hit the backend with the old refresh
 * cookie twice in parallel - which would 401 the loser due to refresh-token
 * rotation in the auth route.
 *
 * Returns:
 *   - AuthResponse on success.
 *   - null when the server *explicitly* rejected the refresh as 401 (the
 *     refresh cookie is missing/invalid/expired - i.e. the session is dead
 *     and the user has to log in again).
 *
 * Throws on every other failure (5xx, network outage, CORS, etc.). Those are
 * transient: the caller should fail the original request the user was making
 * but must NOT log them out, otherwise a flaky backend would punt anyone
 * mid-session straight to /login.
 */
const doRefresh = (): Promise<AuthResponse | null> => {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const { data } = await axios.post<AuthResponse>(
        `${baseURL}/v1/admin/auth/refresh`,
        {},
        { withCredentials: true },
      );
      accessToken = data.accessToken;
      return data;
    } catch (err) {
      const status = (err as AxiosError).response?.status;
      if (status === 401) {
        accessToken = null;
        return null;
      }
      throw err;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
};

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (
      err.response?.status === 401 &&
      original &&
      !original._retried &&
      !original.url?.includes('/v1/admin/auth/')
    ) {
      original._retried = true;
      let fresh: AuthResponse | null;
      try {
        fresh = await doRefresh();
      } catch {
        // Transient refresh failure (5xx, network, etc.). Fail the original
        // request so the caller can surface an error, but leave the session
        // intact - we don't *know* the user is logged out.
        return Promise.reject(err);
      }
      if (fresh) {
        original.headers = {
          ...(original.headers ?? {}),
          Authorization: `Bearer ${fresh.accessToken}`,
        };
        return api.request(original);
      }
      // doRefresh resolved null = explicit 401 = session is genuinely dead.
      onUnauthorised?.();
    }
    return Promise.reject(err);
  },
);

export const login = async (email: string, password: string): Promise<AuthResponse> => {
  const { data } = await api.post<AuthResponse>('/v1/admin/auth/login', { email, password });
  accessToken = data.accessToken;
  return data;
};

export const logout = async (): Promise<void> => {
  try {
    await api.post('/v1/admin/auth/logout');
  } catch {
    // ignore
  }
  accessToken = null;
};

export const fetchMe = async (): Promise<Admin> => {
  const { data } = await api.get<Admin>('/v1/admin/me');
  return data;
};

export const refreshSession = (): Promise<AuthResponse | null> => doRefresh();

export interface OnboardingsListResponse {
  total: number;
  page: number;
  pageSize: number;
  items: OnboardingAdmin[];
}

export const listOnboardings = async (params: {
  status?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}): Promise<OnboardingsListResponse> => {
  const { data } = await api.get<OnboardingsListResponse>('/v1/admin/onboardings', { params });
  return data;
};

export const fetchOnboarding = async (id: string): Promise<OnboardingAdmin> => {
  const { data } = await api.get<OnboardingAdmin>(`/v1/admin/onboardings/${id}`);
  return data;
};

export const createOnboarding = async (input: CreateOnboardingInput): Promise<OnboardingAdmin> => {
  const { data } = await api.post<OnboardingAdmin>('/v1/admin/onboardings', input);
  return data;
};

export const resendOnboarding = async (id: string): Promise<void> => {
  await api.post(`/v1/admin/onboardings/${id}/resend`);
};

export const revokeOnboarding = async (id: string): Promise<void> => {
  await api.post(`/v1/admin/onboardings/${id}/revoke`);
};

export const downloadNashCert = async (id: string): Promise<void> => {
  const { data } = await api.get<{ filename: string; contentBase64: string }>(
    `/v1/admin/onboardings/${id}/nash-cert`,
  );
  const bytes = Uint8Array.from(atob(data.contentBase64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/x-pkcs12' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = data.filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
